// src/frontend/src/pages/FeaturePage.tsx
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import HomeLayout from '../components/Layout/HomeLayout';

interface FeatureContent {
  title: string;
  subtitle: string;
  heroDescription: string;
  keyBenefits: string[];
  detailedFeatures: {
    title: string;
    description: string;
    icon: string;
  }[];
  useCases: {
    title: string;
    description: string;
    scenario: string;
  }[];
  testimonial?: {
    quote: string;
    author: string;
    role: string;
  };
  ctaTitle: string;
  ctaDescription: string;
}

const featureData: Record<string, FeatureContent> = {
  'ai-coaching': {
    title: 'Real-Time AI Coaching',
    subtitle: 'Get instant feedback as you practice stakeholder conversations',
    heroDescription: 'Unlike other coaching platforms that only provide feedback after the fact, PM Coach analyzes your responses in real-time and provides intelligent coaching tips as you type to improve your stakeholder management skills.',
    keyBenefits: [
      'Instant feedback while you practice',
      'Learn proper phrasing and tone',
      'Build better communication habits',
      'Improve responses before sending'
    ],
    detailedFeatures: [
      {
        title: 'Smart Conversation Analysis',
        description: 'Our AI analyzes your communication style, tone, and approach in real-time, identifying opportunities for improvement before you send your response.',
        icon: '🧠'
      },
      {
        title: 'Contextual Coaching Tips',
        description: 'Get specific, actionable suggestions based on the stakeholder type, scenario context, and your current conversation flow.',
        icon: '💡'
      },
      {
        title: 'Progressive Learning',
        description: 'The system learns your communication patterns and provides increasingly sophisticated feedback as you improve.',
        icon: '📈'
      }
    ],
    useCases: [
      {
        title: 'New PM Building Confidence',
        description: 'Learn how to communicate with authority and professionalism',
        scenario: 'Practice responding to executive pushback on timelines with guided coaching'
      },
      {
        title: 'Senior PM Refining Skills',
        description: 'Polish your communication for complex stakeholder negotiations',
        scenario: 'Master the art of saying "no" while maintaining relationships'
      },
      {
        title: 'Interview Preparation',
        description: 'Perfect your responses to behavioral PM interview questions',
        scenario: 'Practice explaining how you handled difficult stakeholder situations'
      }
    ],
    testimonial: {
      quote: 'The real-time coaching feature helped me reframe my responses before sending them. I went from sounding defensive to being diplomatic and solution-focused.',
      author: 'Sarah Chen',
      role: 'Senior PM at TechCorp'
    },
    ctaTitle: 'Experience Real-Time AI Coaching',
    ctaDescription: 'Start your free trial and see how instant feedback can transform your stakeholder conversations.'
  },
  'stakeholder-simulations': {
    title: 'Dynamic Stakeholder Conversations',
    subtitle: 'Practice with AI stakeholders that adapt to your responses',
    heroDescription: 'Our AI-powered stakeholders don\'t follow scripts. They respond dynamically to your approach, creating realistic practice sessions that prepare you for any situation.',
    keyBenefits: [
      'Realistic stakeholder personalities',
      'Adaptive conversation flows',
      '8 different stakeholder types',
      'Industry-specific scenarios'
    ],
    detailedFeatures: [
      {
        title: 'Personality-Driven Responses',
        description: 'Each AI stakeholder has a unique personality, communication style, and set of concerns that influence how they respond to your approach.',
        icon: '🎭'
      },
      {
        title: 'Dynamic Conversation Paths',
        description: 'Unlike branching scenarios with predetermined outcomes, our conversations adapt based on your responses, creating unique practice experiences.',
        icon: '🔄'
      },
      {
        title: 'Emotional Intelligence',
        description: 'Stakeholders react emotionally to your communication style, helping you understand the impact of your words and tone.',
        icon: '❤️'
      }
    ],
    useCases: [
      {
        title: 'Difficult Executive Sponsor',
        description: 'Practice managing an impatient, results-driven executive',
        scenario: 'Navigate budget cuts while maintaining project scope'
      },
      {
        title: 'Frustrated Team Member',
        description: 'Handle team conflicts and motivation issues',
        scenario: 'Address a developer who feels overworked and undervalued'
      },
      {
        title: 'Demanding Client',
        description: 'Manage external stakeholder expectations',
        scenario: 'Explain why additional features will impact timeline and budget'
      }
    ],
    ctaTitle: 'Practice with Dynamic AI Stakeholders',
    ctaDescription: 'Experience conversations that feel real and prepare you for any stakeholder situation.'
  },
  'learning-tracks': {
    title: 'Structured Learning Tracks',
    subtitle: 'Career-focused paths for PM skill development',
    heroDescription: 'Our Learning Tracks provide structured, progressive scenarios designed specifically for your PM career stage. Each track builds upon previous skills and prepares you for your next career milestone.',
    keyBenefits: [
      'Career-stage specific content',
      'Progressive difficulty levels',
      'Tracked skill development',
      'Certification-ready competencies'
    ],
    detailedFeatures: [
      {
        title: 'New PM Essentials',
        description: 'Master fundamental stakeholder conversations every PM needs to know, from team standups to client check-ins.',
        icon: '🌱'
      },
      {
        title: 'Senior PM Challenges',
        description: 'Advanced scenarios covering executive communication, strategic negotiations, and cross-functional leadership.',
        icon: '🏆'
      },
      {
        title: 'PMO Leadership Track',
        description: 'Portfolio-level conversations including resource allocation, strategic planning, and organizational change management.',
        icon: '🎯'
      }
    ],
    useCases: [
      {
        title: 'Career Advancement',
        description: 'Build skills needed for your next promotion',
        scenario: 'Complete the Senior PM track to demonstrate readiness for leadership roles'
      },
      {
        title: 'Role Transition',
        description: 'Prepare for a new type of PM role',
        scenario: 'Use the PMO track to transition from individual contributor to people manager'
      },
      {
        title: 'Performance Review Prep',
        description: 'Document your professional development',
        scenario: 'Export certificates and progress reports for annual reviews'
      }
    ],
    ctaTitle: 'Start Your Learning Journey',
    ctaDescription: 'Choose the track that matches your career stage and start building the skills you need to advance.'
  },
'performance-analytics': {
  title: 'Performance Analytics & Progress Tracking',
  subtitle: 'Measure your PM skill development with detailed analytics',
  heroDescription: 'Transform feedback into actionable insights with comprehensive performance analytics. Track your progress, identify skill gaps, and demonstrate professional growth with data-driven reports designed specifically for project management competencies.',
  keyBenefits: [
    'Detailed performance scoring across PM competencies',
    'Historical progress tracking and trend analysis',
    'Exportable PDF reports for performance reviews',
    'Skill gap identification and improvement recommendations'
  ],
  detailedFeatures: [
    {
      title: 'Comprehensive Scoring System',
      description: 'Every conversation is scored across multiple PM competency frameworks including stakeholder management, communication effectiveness, problem-solving, and leadership skills.',
      icon: '📊'
    },
    {
      title: 'Progress Tracking Dashboard',
      description: 'Visualize your improvement over time with interactive charts showing score trends, skill development, and achievement milestones.',
      icon: '📈'
    },
    {
      title: 'Exportable Reports',
      description: 'Generate professional PDF reports summarizing your performance that you can share with managers during performance reviews or include in promotion discussions.',
      icon: '📄'
    }
  ],
  useCases: [
    {
      title: 'Performance Reviews',
      description: 'Demonstrate concrete skill development to your manager',
      scenario: 'Export detailed analytics showing 40% improvement in stakeholder communication over 6 months'
    },
    {
      title: 'Promotion Preparation',
      description: 'Build a data-driven case for career advancement',
      scenario: 'Show consistent high performance in senior-level scenarios to support promotion to Senior PM'
    },
    {
      title: 'Personal Development Planning',
      description: 'Identify specific areas for focused improvement',
      scenario: 'Analytics reveal struggles with scope management - focus training on boundary-setting scenarios'
    }
  ],
  testimonial: {
    quote: 'The performance analytics helped me identify exactly where I needed improvement. I used the reports in my performance review and got promoted to Senior PM within 6 months.',
    author: 'David Kim',
    role: 'Senior PM at FinTech Startup'
  },
  ctaTitle: 'Start Tracking Your PM Growth',
  ctaDescription: 'Turn practice into measurable professional development with comprehensive performance analytics.'
},
'team-management': {
  title: 'Team Management & Admin Features',
  subtitle: 'Scale PM coaching across your entire organization',
  heroDescription: 'Empower your PMO or L&D team with comprehensive tools to manage, track, and optimize PM coaching across your organization. From individual progress tracking to team-wide analytics, everything you need to build a world-class PM team.',
  keyBenefits: [
    'Centralized team dashboard with usage analytics',
    'Bulk member management and seat allocation',
    'Team-wide progress reporting and benchmarking',
    'Custom content creation for organization-specific scenarios'
  ],
  detailedFeatures: [
    {
      title: 'Admin Dashboard',
      description: 'Get a bird\'s-eye view of your team\'s coaching engagement, progress, and performance with intuitive dashboards designed for PMO leaders and L&D administrators.',
      icon: '👥'
    },
    {
      title: 'Team Analytics',
      description: 'Track team-wide metrics including average scores, most popular scenarios, skill gaps across the organization, and ROI on coaching investment.',
      icon: '📊'
    },
    {
      title: 'Custom Content Management',
      description: 'Create organization-specific scenarios, upload custom stakeholder personas, and tailor coaching content to your company\'s unique project management challenges.',
      icon: '⚙️'
    }
  ],
  useCases: [
    {
      title: 'PMO Skill Development',
      description: 'Scale PM training across large organizations',
      scenario: 'Manage 200+ PMs with standardized skill assessments and targeted development paths'
    },
    {
      title: 'L&D Program Integration',
      description: 'Integrate with existing learning and development initiatives',
      scenario: 'Track completion rates and skill improvements as part of formal PM certification programs'
    },
    {
      title: 'Cross-Team Benchmarking',
      description: 'Compare performance across different teams and departments',
      scenario: 'Identify which teams excel at stakeholder management and share best practices organization-wide'
    }
  ],
  testimonial: {
    quote: 'PM Coach\'s team management features transformed how we develop our 150+ project managers. We can now track skill development at scale and demonstrate clear ROI on our coaching investment.',
    author: 'Maria Rodriguez',
    role: 'VP of PMO at Global Consulting Firm'
  },
  ctaTitle: 'Scale Your PM Coaching Program',
  ctaDescription: 'Contact our team to learn how enterprise features can transform your organization\'s PM development.'
}
// Add more features as needed
};

const FeaturePage: React.FC = () => {
  const { featureName } = useParams<{ featureName: string }>();
  
  if (!featureName || !featureData[featureName]) {
    return (
      <HomeLayout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Feature Not Found</h1>
          <p className="text-gray-600 mb-8">The feature you're looking for doesn't exist.</p>
          <Link to="/" className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">
            Back to Home
          </Link>
        </div>
      </HomeLayout>
    );
  }

  const feature = featureData[featureName];

  return (
    <HomeLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="py-16 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6">
            {feature.title}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {feature.subtitle}
          </p>
          <p className="text-lg text-gray-700 max-w-4xl mx-auto mb-8">
            {feature.heroDescription}
          </p>
          <Link
            to="/register"
            className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors text-lg"
          >
            Try Free for 14 Days
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>

        {/* Key Benefits */}
        <div className="py-16 bg-gray-50 rounded-2xl mb-16">
          <div className="max-w-4xl mx-auto px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Why {feature.title}?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {feature.keyBenefits.map((benefit, index) => (
                <div key={index} className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700 text-lg">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Detailed Features */}
        <div className="py-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {feature.detailedFeatures.map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Use Cases */}
        <div className="py-16 bg-blue-50 rounded-2xl">
          <div className="max-w-6xl mx-auto px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Perfect For
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {feature.useCases.map((useCase, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{useCase.title}</h3>
                  <p className="text-gray-600 mb-4">{useCase.description}</p>
                  <div className="bg-gray-50 p-3 rounded text-sm text-gray-700">
                    <strong>Example:</strong> {useCase.scenario}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonial */}
        {feature.testimonial && (
          <div className="py-16">
            <div className="max-w-4xl mx-auto text-center">
              <blockquote className="text-2xl font-medium text-gray-900 mb-6">
                "{feature.testimonial.quote}"
              </blockquote>
              <div className="text-gray-600">
                <div className="font-semibold">{feature.testimonial.author}</div>
                <div>{feature.testimonial.role}</div>
              </div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl text-white text-center">
          <h2 className="text-3xl font-bold mb-4">{feature.ctaTitle}</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {feature.ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-medium rounded-lg hover:bg-gray-100"
            >
              Start Free Trial
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center px-8 py-3 border-2 border-white text-white rounded-lg hover:bg-white hover:text-blue-600"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </div>
    </HomeLayout>
  );
};

export default FeaturePage;